import React, { useState, useEffect } from 'react';
import './Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const maxVisiblePages = isMobile ? 5 : 5;
  
  // Calcular el rango de páginas a mostrar
  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    
    // Ajustar el inicio si estamos cerca del final
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();
  const showStartEllipsis = visiblePages[0] > 1;
  const showEndEllipsis = visiblePages[visiblePages.length - 1] < totalPages;
  
  // Calcular saltos de bloque
  const blockSize = maxVisiblePages;
  const currentBlock = Math.floor((currentPage - 1) / blockSize);
  const canJumpBackward = currentBlock > 0;
  const canJumpForward = (currentBlock + 1) * blockSize < totalPages;

  const jumpBackward = () => {
    const newPage = Math.max(1, currentBlock * blockSize);
    onPageChange(newPage);
  };

  const jumpForward = () => {
    const newPage = Math.min(totalPages, (currentBlock + 1) * blockSize + 1);
    onPageChange(newPage);
  };

  return (
    <div className="pagination">
      {/* Salto hacia atrás por bloques */}
      {canJumpBackward && (
        <button
          className="pagination-button pagination-jump"
          onClick={jumpBackward}
          title={`Ir a página ${Math.max(1, currentBlock * blockSize)}`}
        >
          &lt;&lt;
        </button>
      )}

      {/* Página anterior */}
      <button
        className="pagination-button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        &lt;
      </button>

      {/* Primera página + ellipsis */}
      {showStartEllipsis && (
        <>
          <button
            className="pagination-button"
            onClick={() => onPageChange(1)}
          >
            1
          </button>
          <span className="pagination-ellipsis">...</span>
        </>
      )}

      {/* Páginas visibles */}
      {visiblePages.map(page => (
        <button
          key={page}
          className={`pagination-button ${page === currentPage ? 'active' : ''}`}
          onClick={() => onPageChange(page)}
        >
          {page}
        </button>
      ))}

      {/* Ellipsis + última página */}
      {showEndEllipsis && (
        <>
          <span className="pagination-ellipsis">...</span>
          <button
            className="pagination-button"
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      {/* Página siguiente */}
      <button
        className="pagination-button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>

      {/* Salto hacia adelante por bloques */}
      {canJumpForward && (
        <button
          className="pagination-button pagination-jump"
          onClick={jumpForward}
          title={`Ir a página ${Math.min(totalPages, (currentBlock + 1) * blockSize + 1)}`}
        >
          &gt;&gt;
        </button>
      )}
    </div>
  );
}; 